<script lang="ts">
  import { getUser, logout } from '$lib/auth.svelte';
  import { getUnreadCount } from '$lib/stores/notifications.svelte';
  import { navigate, getCurrentRoute } from '$lib/router.svelte';
  import { api } from '$shared/api';
  import { onLeaderboardUpdate, onUserStatsUpdate, onSocketReconnect } from '$lib/stores/socket.svelte';
  import NotificationDropdown from './NotificationDropdown.svelte';

  const user = $derived(getUser());
  const adminUrl = import.meta.env.VITE_ADMIN_URL || 'https://admin.beatduel.com';
  const unread = $derived(getUnreadCount());
  const route = $derived(getCurrentRoute());

  let menuOpen = $state(false);
  let mobileOpen = $state(false);
  let bellOpen = $state(false);
  let points = $state(0);
  let rank = $state(0);
  let season = $state(0);
  let pointsDelta = $state(0);
  let rankDelta = $state(0);
  let searchQuery = $state('');

  let pointsDeltaTimer: ReturnType<typeof setTimeout> | null = null;
  let rankDeltaTimer: ReturnType<typeof setTimeout> | null = null;
  let leaderboardRefetchTimer: ReturnType<typeof setTimeout> | null = null;

  async function fetchInitialStats() {
    try {
      const d = await api.get<{ points: number; rank: number; season_number: number }>('/users/current');
      points = d.points ?? 0;
      rank = d.rank ?? 0;
      season = d.season_number ?? 0;
    } catch (e) {
      console.warn('Failed to load user stats:', e);
    }
  }

  async function refetchStats() {
    try {
      const d = await api.get<{ points: number; rank: number; season_number: number }>('/users/current');
      if (d.season_number) season = d.season_number;
      applyStats(d.points ?? 0, d.rank ?? 0, 0);
    } catch (e) {
      console.warn('Failed to refresh user stats:', e);
    }
  }

  function applyStats(newPoints: number, newRank: number, delta: number) {
    const pDiff = newPoints - points;
    const rDiff = rank > 0 ? rank - newRank : 0; // positive = moved up the board
    points = newPoints;
    rank = newRank;
    if (delta !== 0 || pDiff !== 0) {
      pointsDelta = delta !== 0 ? delta : pDiff;
      if (pointsDeltaTimer) clearTimeout(pointsDeltaTimer);
      pointsDeltaTimer = setTimeout(() => {
        pointsDelta = 0;
      }, 1500);
    }
    if (rDiff !== 0 && rank > 0) {
      rankDelta = rDiff;
      if (rankDeltaTimer) clearTimeout(rankDeltaTimer);
      rankDeltaTimer = setTimeout(() => {
        rankDelta = 0;
      }, 2000);
    }
  }

  $effect(() => {
    if (!user) {
      points = 0;
      rank = 0;
      season = 0;
      return;
    }
    fetchInitialStats();

    const unsubStats = onUserStatsUpdate((e) => {
      if (e.season_number) season = e.season_number;
      applyStats(e.points, e.rank, e.delta);
    });

    // Others' actions may shift my rank — debounce-refetch to coalesce bursts.
    const unsubLeaderboard = onLeaderboardUpdate(() => {
      if (leaderboardRefetchTimer) clearTimeout(leaderboardRefetchTimer);
      leaderboardRefetchTimer = setTimeout(refetchStats, 1500);
    });

    // After a real socket drop + reconnect, replay any state we may have missed.
    const unsubReconnect = onSocketReconnect(() => {
      refetchStats();
    });

    return () => {
      unsubStats();
      unsubReconnect();
      unsubLeaderboard();
      if (pointsDeltaTimer) clearTimeout(pointsDeltaTimer);
      if (rankDeltaTimer) clearTimeout(rankDeltaTimer);
      if (leaderboardRefetchTimer) clearTimeout(leaderboardRefetchTimer);
    };
  });

  function handleLogout() {
    menuOpen = false;
    mobileOpen = false;
    logout();
  }

  function handleSearch(e: Event) {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      searchQuery = '';
      mobileOpen = false;
    }
  }

  function navClick() {
    mobileOpen = false;
    menuOpen = false;
    bellOpen = false;
  }

  // Active link detection
  const isActive = $derived((prefix: string) => route.path.startsWith(prefix));
</script>

<header class="site-header">
  <div class="container header-inner">
    <a href="#/" class="logo">Beat Duel</a>

    <button
      class="nav-toggle"
      aria-label="Menu"
      onclick={() => {
        mobileOpen = !mobileOpen;
        menuOpen = false;
        bellOpen = false;
      }}
    >
      {mobileOpen ? '\u00d7' : '\u2630'}
    </button>

    <nav class="nav" class:nav--open={mobileOpen}>
      <a
        href="#/duels"
        class="nav-link"
        class:nav-link--active={isActive('/duels') || route.path === '/'}
        onclick={navClick}>Duels</a
      >
      <a href="#/catalog" class="nav-link" class:nav-link--active={isActive('/catalog')} onclick={navClick}
        >My Catalog</a
      >
      <a href="#/friends" class="nav-link" class:nav-link--active={route.path === '/friends'} onclick={navClick}
        >Friends</a
      >
      <a
        href="#/account/invite"
        class="nav-link"
        class:nav-link--active={route.path === '/account/invite'}
        onclick={navClick}>Invite</a
      >
      <a
        href="#/feed"
        class="nav-link nav-link--icon"
        class:nav-link--active={route.path === '/feed'}
        onclick={navClick}
        aria-label="Feed"
        ><svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
          ><path stroke-linecap="round" stroke-linejoin="round" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg
        ></a
      >

      {#if season > 0}
        <span class="nav-season-tag" title="Week {season} — leaderboard resets weekly">W{season}</span>
      {/if}
      <a href="#/points" class="nav-stat" class:nav-link--active={route.path === '/points'} onclick={navClick}>
        {points.toLocaleString()} pts
        {#if pointsDelta !== 0}
          <span class="delta-pop" class:delta-pop--down={pointsDelta < 0}
            >{pointsDelta > 0 ? '+' : ''}{pointsDelta}</span
          >
        {/if}
      </a>
      <a href="#/leaderboard" class="nav-stat" class:nav-link--active={route.path === '/leaderboard'} onclick={navClick}
        >{#if rank > 0}Ranked #{rank}{:else}Unranked{/if}
        {#if rankDelta !== 0}
          <span class="delta-pop" class:delta-pop--down={rankDelta < 0}
            >{rankDelta > 0 ? '▲' : '▼'}{Math.abs(rankDelta)}</span
          >
        {/if}
      </a>

      <div class="bell-wrap">
        <button
          class="bell"
          aria-label="Notifications"
          onclick={() => {
            bellOpen = !bellOpen;
            menuOpen = false;
            mobileOpen = false;
          }}
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          {#if unread > 0}
            <span class="bell-badge">{unread > 99 ? '99+' : unread}</span>
          {/if}
        </button>
        <NotificationDropdown bind:open={bellOpen} />
      </div>

      <div class="user-menu">
        <button
          class="user-trigger"
          aria-label="User menu"
          onclick={() => {
            menuOpen = !menuOpen;
            bellOpen = false;
          }}
        >
          {#if user?.avatar_url}
            <img src={user.avatar_url} alt="{user.username}'s avatar" class="user-avatar" />
          {:else}
            <span class="user-avatar-placeholder">{user?.username?.charAt(0) ?? '?'}</span>
          {/if}
          <span class="user-name">{user?.username} &#9662;</span>
        </button>

        {#if menuOpen}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div class="overlay" role="button" tabindex="-1" onclick={() => (menuOpen = false)}></div>
          <div
            class="dropdown"
            role="menu"
            aria-label="User menu"
            tabindex="-1"
            onkeydown={(e: KeyboardEvent) => {
              if (e.key === 'Escape') {
                menuOpen = false;
              }
            }}
          >
            <a href="#/account" class="dropdown-item" onclick={navClick}>Account</a>
            <a href="#/account/profile" class="dropdown-item" onclick={navClick}>Edit Profile</a>
            <a href="#/account/security" class="dropdown-item" onclick={navClick}>Security</a>
            <a href="#/account/stats" class="dropdown-item" onclick={navClick}>Stats</a>
            <a href="#/account/activity" class="dropdown-item" onclick={navClick}>My Activity</a>
            <a href="#/catalog" class="dropdown-item" onclick={navClick}>My Catalog</a>
            <a href="#/account/genres" class="dropdown-item" onclick={navClick}>My Genres</a>
            {#if user?.is_admin}
              <div class="dropdown-divider"></div>
              <a
                href={adminUrl}
                target="_blank"
                rel="noopener"
                class="dropdown-item dropdown-item--warning"
                onclick={navClick}>Admin ↗</a
              >
            {/if}
            <div class="dropdown-divider"></div>
            <button class="dropdown-item dropdown-item--danger" onclick={handleLogout}>Log Out</button>
          </div>
        {/if}
      </div>

      <form class="nav-search" onsubmit={handleSearch}>
        <label for="nav-search-input" class="sr-only">Search</label>
        <input
          id="nav-search-input"
          type="search"
          class="nav-search__input"
          placeholder="Search..."
          bind:value={searchQuery}
        />
      </form>
    </nav>
  </div>
</header>

<style>
  .site-header {
    border-bottom: 1px solid var(--border);
    background: var(--surface);
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
  .logo {
    white-space: nowrap;
    font-size: 1.6rem;
    font-weight: 800;
    flex-shrink: 0;
    background: linear-gradient(135deg, var(--pink) 0%, var(--purple) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .nav {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .nav-link {
    font-size: 0.875rem;
    color: var(--text-muted);
    font-weight: 500;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }
  .nav-link:hover {
    color: var(--pink);
    -webkit-text-fill-color: var(--pink);
  }
  .nav-link--active {
    color: var(--cyan);
    -webkit-text-fill-color: var(--cyan);
  }
  .nav-link--icon {
    display: flex;
    align-items: center;
  }

  .nav-stat {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--cyan);
    padding: 0.2rem 0.5rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    white-space: nowrap;
  }
  .nav-stat:hover {
    border-color: var(--cyan);
  }
  .nav-stat--bd {
    color: var(--gold, #fbbf24);
  }
  .nav-stat--bd:hover {
    border-color: var(--gold, #fbbf24);
  }
  .nav-season-tag {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: var(--purple);
    background: var(--surface-2);
    padding: 0.2rem 0.45rem;
    border-radius: 999px;
    white-space: nowrap;
  }
  .delta-pop {
    display: inline-block;
    margin-left: 0.25rem;
    padding: 0 0.25rem;
    font-size: 0.7rem;
    font-weight: 700;
    color: var(--success, #4ade80);
    background: rgba(74, 222, 128, 0.12);
    border-radius: 999px;
    animation:
      delta-pop-in 0.25s ease-out,
      delta-pop-out 0.4s ease-in 1.1s both;
  }
  .delta-pop--down {
    color: var(--text-muted);
    background: rgba(255, 255, 255, 0.06);
  }
  @keyframes delta-pop-in {
    from {
      transform: translateY(-4px) scale(0.8);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
  @keyframes delta-pop-out {
    to {
      transform: translateY(-2px);
      opacity: 0;
    }
  }

  .nav-toggle {
    display: none;
    background: none;
    border: none;
    color: var(--text);
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
  }

  .bell-wrap {
    position: relative;
    flex-shrink: 0;
  }
  .bell {
    position: relative;
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 0;
    transform: none;
    flex-shrink: 0;
  }
  .bell:hover {
    color: var(--text);
    opacity: 1;
    transform: none;
  }
  .bell-badge {
    position: absolute;
    top: -4px;
    right: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    background: var(--error);
    border-radius: 999px;
  }

  .user-menu {
    position: relative;
    flex-shrink: 0;
  }
  .user-trigger {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 0.875rem;
    padding: 0;
    transform: none;
  }
  .user-trigger:hover {
    color: var(--text);
    opacity: 1;
    transform: none;
  }
  .user-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }
  .user-avatar-placeholder {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--surface-2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
  }
  .user-name {
    display: none;
  }
  @media (min-width: 768px) {
    .user-name {
      display: inline;
    }
  }

  .overlay {
    position: fixed;
    inset: 0;
    z-index: 40;
  }
  .dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 0.5rem;
    z-index: 50;
    width: 12rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.25rem 0;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }
  .dropdown-item {
    display: block;
    width: 100%;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    text-align: left;
    background: none;
    border: none;
    color: var(--text);
    cursor: pointer;
    transform: none;
    letter-spacing: 0;
    font-weight: 400;
    opacity: 1;
  }
  .dropdown-item:hover {
    background: var(--surface-2);
    color: var(--text);
    opacity: 1;
    transform: none;
  }
  .dropdown-item--warning {
    color: var(--warning);
  }
  .dropdown-item--danger {
    color: var(--error);
  }
  .dropdown-divider {
    margin: 0.25rem 0;
    border-top: 1px solid var(--border);
  }

  .nav-search {
    display: flex;
    flex-shrink: 0;
  }
  .nav-search__input {
    width: 120px;
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    color: var(--text);
    outline: none;
  }
  .nav-search__input:focus {
    border-color: var(--pink);
    width: 160px;
  }
  .nav-search__input::placeholder {
    color: var(--text-muted);
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  @media (max-width: 767px) {
    .nav-toggle {
      display: block;
    }
    .nav {
      display: none;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 30;
      background: var(--surface);
      border-bottom: 1px solid var(--border);
      flex-direction: column;
      align-items: stretch;
      padding: 0.75rem 1rem;
      gap: 0.5rem;
    }
    .nav--open {
      display: flex;
    }
    .nav-link,
    .nav-stat {
      font-size: 1rem;
      padding: 0.5rem 0;
    }
    .nav-search__input {
      width: 100%;
    }
    .nav-search__input:focus {
      width: 100%;
    }
    .user-name {
      display: inline;
    }
  }
</style>
